import { useId, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  OutlinedInput,
  InputAdornment,
  Popover,
  Slider,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useRewampT } from "../../../localisation";
import DropdownArrowIcon from "./DropdownArrowIcon";
import { dropdownPartSx } from "../../../design/recipes/dropdown";
import { useFilterCellAnchor } from "./FilterFieldCell";

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const groupDigits = (n) => (n === "" ? "" : Number(n).toLocaleString("en-IN"));
const digitsOf = (text) => String(text).replace(/\D/g, "");

// Read-only outlined input (inherits the multiselect chrome) opening a [min, max] panel:
// two grouped number fields + a two-thumb slider over [bounds].
export default function FilterRangeDropdownField({
  controlId,
  labelKey,
  label,
  panelTitleKey,
  placeholderKey,
  bounds,
  value,
  onChange,
  formatValue,
  dropdownVariant,
}) {
  const tRewamp = useRewampT();
  const resolvedLabel = label || (labelKey ? tRewamp(labelKey) : undefined);
  const placeholder = placeholderKey ? tRewamp(placeholderKey) : resolvedLabel;
  const panelTitle = panelTitleKey ? tRewamp(panelTitleKey) : resolvedLabel;
  const fallbackId = useId();
  const safeId = controlId || fallbackId;
  const [triggerEl, setTriggerEl] = useState(null);
  const [drafts, setDrafts] = useState({});
  const { anchorEl: cellEl, gap } = useFilterCellAnchor();
  const open = Boolean(triggerEl);
  const anchorEl = open && dropdownVariant && cellEl ? cellEl : triggerEl;
  const [lo, hi] = bounds;
  const isUntouched = value[0] <= lo && value[1] >= hi;
  const openAt = (event) => setTriggerEl(event.currentTarget);

  const commit = ([a, b]) => {
    const x = clamp(a, lo, hi);
    const y = clamp(b, lo, hi);
    onChange(x <= y ? [x, y] : [y, x]);
  };
  // Typed values are held as drafts and committed on blur, so a half-typed min never trips the max.
  const commitDraft = (index) => {
    const text = drafts[index];
    setDrafts((prev) => {
      const rest = { ...prev };
      delete rest[index];
      return rest;
    });
    if (text === undefined) return;
    const next = [...value];
    next[index] = text === "" ? bounds[index] : Number(text);
    commit(next);
  };

  const renderInput = (index, ariaKey) => {
    const inputId = `${safeId}-${index ? "max" : "min"}`;
    const text = groupDigits(drafts[index] ?? value[index]);
    return (
      <Box
        sx={{
          flex: "0 0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <OutlinedInput
          id={inputId}
          size="small"
          value={text}
          onChange={(event) =>
            setDrafts((prev) => ({
              ...prev,
              [index]: digitsOf(event.target.value),
            }))
          }
          onBlur={() => commitDraft(index)}
          onKeyDown={(event) => {
            if (event.key === "Enter") commitDraft(index);
          }}
          inputProps={{
            inputMode: "numeric",
            // Hug the value: an <input> never shrinks to its text on its own.
            style: { width: `${Math.max(text.length, 1)}ch` },
            "aria-label": `${resolvedLabel ?? ""} ${tRewamp(ariaKey)}`.trim(),
          }}
          sx={dropdownPartSx(dropdownVariant, "rangeValueBox")}
        />
        <Typography
          component="label"
          htmlFor={inputId}
          variant="caption"
          color="text.textGrey"
          sx={{ textAlign: "center" }}
        >
          {tRewamp(ariaKey)}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <OutlinedInput
        id={safeId}
        readOnly
        fullWidth
        value={
          isUntouched
            ? ""
            : `${formatValue(value[0])} – ${formatValue(value[1])}`
        }
        placeholder={placeholder}
        onClick={openAt}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" ||
            event.key === " " ||
            event.key === "ArrowDown"
          ) {
            event.preventDefault();
            openAt(event);
          }
        }}
        inputProps={{
          role: "button",
          "aria-haspopup": "dialog",
          "aria-expanded": open,
          "aria-label": resolvedLabel,
        }}
        endAdornment={
          <InputAdornment position="end">
            {dropdownVariant ? (
              <DropdownArrowIcon open={open} />
            ) : (
              <KeyboardArrowDownIcon
                aria-hidden="true"
                sx={{
                  color: "action.active",
                  transform: open ? "rotate(180deg)" : "none",
                }}
              />
            )}
          </InputAdornment>
        }
        sx={[
          { "& input": { cursor: "pointer" } },
          dropdownPartSx(dropdownVariant, "trigger"),
        ].filter(Boolean)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setTriggerEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={dropdownPartSx(dropdownVariant, "popper")}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": panelTitle,
            variant: dropdownVariant ? "filterDropdownPanel" : undefined,
            sx: (theme) => ({
              p: 2,
              mt: gap,
              width: anchorEl?.offsetWidth,
              minWidth: theme.spacing(30),
            }),
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="subtitle2" component="h3">
            {panelTitle}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            {renderInput(0, "FILTER_ROW.MIN")}
            {renderInput(1, "FILTER_ROW.MAX")}
          </Box>
          <Slider
            id={`${safeId}-slider`}
            value={value}
            onChange={(event, next) => commit(next)}
            min={lo}
            max={hi}
            disableSwap
            valueLabelDisplay="auto"
            valueLabelFormat={formatValue}
            getAriaLabel={(index) =>
              `${resolvedLabel ?? ""} ${tRewamp(index ? "FILTER_ROW.MAX" : "FILTER_ROW.MIN")}`.trim()
            }
            getAriaValueText={formatValue}
            sx={dropdownPartSx(dropdownVariant, "rangeSlider")}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.textGrey">
              {formatValue(lo)}
            </Typography>
            <Typography variant="caption" color="text.textGrey">
              {formatValue(hi)}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

FilterRangeDropdownField.propTypes = {
  controlId: PropTypes.string,
  labelKey: PropTypes.string,
  label: PropTypes.string,
  panelTitleKey: PropTypes.string,
  placeholderKey: PropTypes.string,
  bounds: PropTypes.arrayOf(PropTypes.number).isRequired,
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  formatValue: PropTypes.func,
  /** Dropdown variant from dropdownVocabulary.js; omit for the stock outlined-input chrome. */
  dropdownVariant: PropTypes.string,
};

FilterRangeDropdownField.defaultProps = {
  controlId: undefined,
  labelKey: null,
  label: null,
  panelTitleKey: null,
  placeholderKey: null,
  formatValue: (v) => v,
  dropdownVariant: undefined,
};
